import { LegalDocumentContent } from "@/components/legal/LegalDocumentContent";
import { LoadingSpinner } from "@/components/shared/LoadingSpinner";
import { StaticPageLayout } from "@/components/layout/StaticPageLayout";
import { AppCard } from "@/components/ui/AppCard";
import { Button, RouteLink, Typography } from "@/components/ui";
import { TLegalDocument } from "@/api/legal";
import { Home } from "lucide-react";
import type { ReactNode } from "react";

type TLegalDocumentViewProps = {
  document: TLegalDocument | null;
  isLoading: boolean;
  errorMessage?: string | null;
  pageTitle: string;
  /** Optional footer action (e.g. accept flow). Defaults to Back to Home. */
  footerAction?: ReactNode;
};

export const LegalDocumentView = ({
  document,
  isLoading,
  errorMessage,
  pageTitle,
  footerAction,
}: TLegalDocumentViewProps) => {
  const effectiveDate = document?.effective_at
    ? new Date(document.effective_at).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  return (
    <StaticPageLayout variant="document">
      <div className="container mx-auto flex min-h-0 w-full max-w-4xl flex-1 flex-col gap-5">
        <header className="shrink-0 space-y-2 text-center">
          <Typography variant="h4" className="tracking-tight text-white sm:text-2xl">
            {pageTitle}
          </Typography>
          {document?.title && document.title !== pageTitle ? (
            <Typography variant="p" affects="muted" className="text-sm text-white/80">
              {document.title}
            </Typography>
          ) : null}
          {(effectiveDate ?? document?.version) && (
            <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-xs text-white/75 sm:text-sm">
              {effectiveDate ? <span>Effective {effectiveDate}</span> : null}
              {effectiveDate && document?.version ? (
                <span className="text-white/40" aria-hidden="true">·</span>
              ) : null}
              {document?.version ? <span>Version {document.version}</span> : null}
            </div>
          )}
        </header>

        <AppCard
          variant="elevated"
          padding="none"
          className="flex min-h-0 flex-1 flex-col overflow-hidden border-border/80 bg-card shadow-md"
        >
          <div className="legal-document-scroll min-h-0 flex-1 overflow-y-auto px-5 py-6 sm:px-8 sm:py-8">
            {isLoading ? (
              <div className="flex justify-center py-16">
                <LoadingSpinner />
              </div>
            ) : null}

            {!isLoading && errorMessage ? (
              <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
                <Typography variant="h5" className="text-foreground">Unable to load document</Typography>
                <Typography variant="p" affects="muted" className="max-w-md text-sm">
                  {errorMessage}
                </Typography>
              </div>
            ) : null}

            {!isLoading && document ? (
              <LegalDocumentContent content={document.content} />
            ) : null}
          </div>
        </AppCard>

        <footer className="shrink-0 pb-2 text-center">
          {footerAction ?? (
            <RouteLink to="/">
              <Button size="lg" className="px-8">
                <Home className="mr-2 h-5 w-5" />
                Back to Home
              </Button>
            </RouteLink>
          )}
        </footer>
      </div>
    </StaticPageLayout>
  );
};
